'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';

/* ─── Types ─────────────────────────────────────────────────── */
type Lang = 'en' | 'ar' | 'fr';

interface ActivityMessage {
  title: string;
  body: string;
}

/* ─── Demo Activity Messages ────────────────────────────────── */
const activityMessages: Record<Lang, ActivityMessage[]> = {
  en: [
    { title: 'Recent Activity', body: 'Growing interest in this plan.' },
    { title: 'Demo Activity', body: 'Users from around the world are exploring the Financial Freedom plan.' },
    { title: 'Demo Activity', body: 'Wealth Mastery is attracting significant attention right now.' },
    { title: 'Recent Activity', body: 'Many visitors are exploring WealthOS right now.' },
    { title: 'Demo Activity', body: 'A visitor from France is exploring WealthOS right now.' },
    { title: 'Demo Activity', body: 'A visitor from Canada is viewing the Financial Freedom plan.' },
  ],
  ar: [
    { title: 'نشاط حديث', body: 'هناك اهتمام متزايد بهذه الباقة.' },
    { title: 'نشاط تجريبي', body: 'يستكشف مستخدمون من مختلف أنحاء العالم باقة الحرية المالية.' },
    { title: 'نشاط تجريبي', body: 'باقة Wealth Mastery تحظى باهتمام كبير حالياً.' },
    { title: 'نشاط حديث', body: 'عدد كبير من الزوار يستكشفون WealthOS الآن.' },
    { title: 'نشاط تجريبي', body: 'زائر من فرنسا يستكشف WealthOS الآن.' },
    { title: 'نشاط تجريبي', body: 'زائر من كندا يستكشف باقة الحرية المالية.' },
  ],
  fr: [
    { title: 'Activité récente', body: 'Cette offre suscite actuellement beaucoup d\'intérêt.' },
    { title: 'Activité démo', body: 'Des utilisateurs du monde entier découvrent actuellement l\'offre Financial Freedom.' },
    { title: 'Activité démo', body: 'Wealth Mastery attire actuellement beaucoup d\'attention.' },
    { title: 'Activité récente', body: 'De nombreux visiteurs découvrent WealthOS en ce moment.' },
    { title: 'Activité démo', body: 'Un visiteur en France découvre actuellement WealthOS.' },
    { title: 'Activité démo', body: 'Un visiteur au Canada consulte actuellement l\'offre Financial Freedom.' },
  ],
};

/* ─── Analytics Helper ──────────────────────────────────────── */
function trackEvent(event: string, lang: Lang) {
  if (typeof window !== 'undefined' && (window as any).gtag) {
    (window as any).gtag('event', event, {
      event_category: 'activity_notification',
      event_label: lang,
      language: lang,
    });
  }
}

/* ─── ActivityNotification Component ───────────────────────── */
interface ActivityNotificationProps {
  lang: Lang;
  isRtl: boolean;
}

export default function ActivityNotification({ lang, isRtl }: ActivityNotificationProps) {
  const [visible, setVisible] = useState(false);
  const [currentMsg, setCurrentMsg] = useState<ActivityMessage | null>(null);
  const [msgIndex, setMsgIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showNotification = useCallback((lang: Lang, index: number) => {
    const messages = activityMessages[lang];
    const msg = messages[index % messages.length];
    setCurrentMsg(msg);
    setVisible(true);
    trackEvent('activity_notification_viewed', lang);

    const displayDuration = 4000 + Math.random() * 2000;
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      setVisible(false);
    }, displayDuration);
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const initialDelay = 8000 + Math.random() * 4000;

    showTimerRef.current = setTimeout(() => {
      showNotification(lang, msgIndex);

      const scheduleNext = (currentIndex: number) => {
        const interval = 30000 + Math.random() * 30000;
        showTimerRef.current = setTimeout(() => {
          const nextIndex = currentIndex + 1;
          setMsgIndex(nextIndex);
          showNotification(lang, nextIndex);
          scheduleNext(nextIndex);
        }, interval);
      };

      scheduleNext(msgIndex);
    }, initialDelay);

    return () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [mounted]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (visible && currentMsg) {
      const messages = activityMessages[lang];
      setCurrentMsg(messages[msgIndex % messages.length]);
    }
  }, [lang]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!mounted || !currentMsg) return null;

  return (
    <div
      className={`fixed z-[55] transition-all duration-500 ease-out pointer-events-none
        bottom-16 sm:bottom-20
        ${isRtl ? 'right-3 sm:right-4 left-auto' : 'left-3 sm:left-4 right-auto'}
        ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
      `}
      style={{
        /* Respect safe areas */
        bottom: `calc(${isRtl ? '4rem' : '4rem'} + env(safe-area-inset-bottom, 0px))`,
        maxWidth: 'calc(100vw - 1.5rem)',
      }}
      dir={isRtl ? 'rtl' : 'ltr'}
      aria-live="polite"
      aria-atomic="true"
    >
      <div
        className="w-[260px] xs:w-[280px] sm:w-[300px] glass-card rounded-2xl border border-border/60 bg-background/95 backdrop-blur-xl shadow-2xl p-3 sm:p-3.5"
        style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.3), 0 0 0 1px rgba(201,162,39,0.1)' }}
      >
        <div className={`flex items-start gap-2 sm:gap-2.5 ${isRtl ? 'flex-row-reverse' : ''}`}>
          {/* Icon */}
          <div className="w-7 h-7 rounded-full bg-primary/15 border border-primary/25 flex items-center justify-center flex-shrink-0 mt-0.5">
            <span className="text-xs">👁️</span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className={`flex items-center gap-1.5 mb-0.5 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <span className="text-[10px] font-bold text-primary uppercase tracking-wide truncate">
                {currentMsg.title}
              </span>
              <span className="w-1 h-1 rounded-full bg-primary/50 flex-shrink-0" />
              <span className="text-[9px] text-muted-foreground flex-shrink-0">
                {isRtl ? 'الآن' : lang === 'fr' ? 'maintenant' : 'now'}
              </span>
            </div>
            <p className={`text-xs text-foreground/80 leading-relaxed ${isRtl ? 'text-right' : 'text-left'}`}>
              {currentMsg.body}
            </p>
          </div>
        </div>

        {/* Animated bottom bar */}
        <div className="mt-2 sm:mt-2.5 h-0.5 rounded-full bg-border/40 overflow-hidden">
          <div
            className="h-full bg-primary/40 rounded-full"
            style={{
              animation: visible ? 'shrink-bar 5s linear forwards' : 'none',
            }}
          />
        </div>
      </div>

      <style>{`
        @keyframes shrink-bar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>
    </div>
  );
}
